import { useEffect, useRef, useState } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import { Input } from '@/shared/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { CHAT_COPY, chatErrorMessage, useChatUiStore, useCreateGroup, type DirectoryEntry } from '@/entities/chat'
import { PersonPicker } from './PersonPicker'

const GROUP_CAPACITY = 50

export function NewGroupDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: (conversationId: number) => void
}) {
  const [title, setTitle] = useState('')
  const [selected, setSelected] = useState<DirectoryEntry[]>([])
  const [nameError, setNameError] = useState<string | null>(null)
  const [membersError, setMembersError] = useState<string | null>(null)
  const [capacityError, setCapacityError] = useState<string | null>(null)
  const [serverError, setServerError] = useState<string | null>(null)

  const createGroup = useCreateGroup()
  const setTriage = useChatUiStore((state) => state.setTriage)
  const pickerInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) return
    setTitle('')
    setSelected([])
    setNameError(null)
    setMembersError(null)
    setCapacityError(null)
    setServerError(null)
  }, [open])

  const handlePick = (entry: DirectoryEntry) => {
    setCapacityError(null)
    const alreadySelected = selected.some((item) => item.userId === entry.userId)
    if (alreadySelected) {
      setSelected((current) => current.filter((item) => item.userId !== entry.userId))
      return
    }
    if (selected.length + 1 >= GROUP_CAPACITY) {
      setCapacityError(CHAT_COPY.groupAtCapacity)
      return
    }
    setSelected((current) => [...current, entry])
  }

  const removeSelected = (userId: number) => {
    setSelected((current) => current.filter((item) => item.userId !== userId))
  }

  const handleCreate = () => {
    const trimmedTitle = title.trim()
    let blocked = false

    if (trimmedTitle === '') {
      setNameError(CHAT_COPY.groupNeedsName)
      blocked = true
    } else {
      setNameError(null)
    }

    if (selected.length === 0) {
      setMembersError(CHAT_COPY.groupNeedsMembers)
      blocked = true
    } else {
      setMembersError(null)
    }

    if (blocked) return

    setServerError(null)
    createGroup.mutate(
      { title: trimmedTitle, userIds: selected.map((entry) => entry.userId) },
      {
        onSuccess: (conversation) => {
          setTriage('all')
          onOpenChange(false)
          onCreated(conversation.id)
        },
        onError: (error) => setServerError(chatErrorMessage(error, CHAT_COPY.groupCreateFailed)),
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{CHAT_COPY.newGroupTitle}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="new-group-name" className="text-[12.5px] font-medium text-ink-2">
            {CHAT_COPY.groupNameLabel}
          </label>
          <Input
            id="new-group-name"
            autoFocus
            value={title}
            maxLength={120}
            placeholder={CHAT_COPY.groupNamePlaceholder}
            onChange={(event) => setTitle(event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== 'Enter') return
              event.preventDefault()
              pickerInputRef.current?.focus()
            }}
          />
          {nameError && <p className="text-[12px] text-bad">{nameError}</p>}
        </div>

        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <span className="text-[12.5px] font-medium text-ink-2">{CHAT_COPY.groupPeopleLabel}</span>
            <span className="text-[11.5px] text-ink-4">{CHAT_COPY.groupCapacity(selected.length + 1)}</span>
          </div>
          {selected.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {selected.map((entry) => (
                <Badge key={entry.userId} variant="secondary" className="gap-1 pr-1">
                  {entry.name}
                  <button
                    type="button"
                    aria-label={`Remove ${entry.name}`}
                    onClick={() => removeSelected(entry.userId)}
                    className="rounded-full p-0.5 hover:bg-hover"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}
          {membersError && <p className="text-[12px] text-bad">{membersError}</p>}
          {capacityError && <p className="text-[12px] text-bad">{capacityError}</p>}
          <PersonPicker
            selectedIds={selected.map((entry) => entry.userId)}
            excludedIds={[]}
            onPick={handlePick}
            inputRef={pickerInputRef}
          />
        </div>

        {serverError && <p className="text-[12.5px] text-bad">{serverError}</p>}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={createGroup.isPending}>
            {CHAT_COPY.deleteCancel}
          </Button>
          <Button onClick={handleCreate} disabled={createGroup.isPending}>
            {CHAT_COPY.groupCreateAction}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
